import { ColumnProps, TablePaginationConfig, TableProps } from 'antd/lib/table';
import { ColumnType, SorterResult, SortOrder, TableRowSelection } from 'antd/lib/table/interface';
import { GetComponentProps } from 'rc-table/lib/interface';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { Skeleton, Table, StarToggler, Avatar, TenderStatusBadge, Tooltip } from 'src/common';
import { scrollToItem } from 'src/helpers/scrollToElement';
import { TendersSearchParams } from 'src/models/procurements/Tenders/types';
import { Key } from 'antd/es/table/interface';
import { ActivePreviewTab, ProcurementsDisplay } from 'src/models/procurements/types';
import styles from './index.module.scss';
import Deadline from './Deadline';
import Published from './Published';
import RowComponent from './Dnd';
import classNames from 'classnames';
import OverviewActionsMultiple from './ActionsMultiple';
import OverviewActionsSingle from './ActionsSingle';
import EmptyMessage from './EmptyMessage';
import { getNextOffset, isSearchApplied } from 'src/models/procurements/Tenders/helpers';
import { useFetchMore } from 'src/common/infinityScroll';
import { useCurrentSidebar } from 'src/shared/InfoSidebar/hooks';
import { useCurrentFromProc, useOpenTenderPreview, useTendersViewState } from 'src/models/procurements/hooks';
import EmptySearchMessage from './EmptyMessage/EmptySearchMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots } from '@fortawesome/pro-light-svg-icons';
import { LoadMoreButton, ViewOptionsDropDown, TendersTotal, FilterStatuses } from 'src/shared';
import useSelectedTenders from 'src/reactiveVars/SelectedTendersVar';
import TenderReportBadge from 'src/shared/Tender/TenderReportBadge';
import { useUpdateIsStarredTender } from 'src/models/procurements/hooks';
import { PageView } from 'src/models/users/Preferences/types';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import { TendersTableItem, useTendersTable } from 'src/models/procurements/TendersViews';
import {
  TendersSortName,
  TendersSortOrder,
  TendersSortVars,
  isTendersSortName
} from 'src/models/procurements/Tenders/types';
import useOverviewMode from 'src/reactiveVars/overviewModeVar';

function mapSortingToSortOrder(column: TendersSortName, sorting?: TendersSortVars): SortOrder {
  if (sorting === null) {
    return null;
  }
  if (sorting?.key === column && isTendersSortName(column)) {
    switch (sorting.sort) {
      case TendersSortOrder.ASCENDING: {
        return 'ascend';
      }
      case TendersSortOrder.DESCENDING: {
        return 'descend';
      }
    }
  }
  return null;
}
const prepareSortData = (sorter: SorterResult<TendersTableItem>): TendersSortVars | undefined => {
  const { order } = sorter;
  const apiOrder = order === 'ascend' ? TendersSortOrder.ASCENDING : TendersSortOrder.DESCENDING;
  const column = sorter.column;
  return column && column.key && isTendersSortName(column.key) && order
    ? { key: column.key as TendersSortName, sort: apiOrder }
    : undefined;
};

function useTableColumns(
  onSetStarred: (id: string, name: string, starred: boolean) => void,
  onGoToPreview: (procurementId: string, tab?: ActivePreviewTab) => void,
  sorting?: TendersSortVars
): ColumnProps<TendersTableItem>[] {
  const { t } = useTranslation();

  return useMemo(() => {
    return [
      {
        title: <OverviewActionsMultiple />,
        dataIndex: 'actions',
        key: 'actions',
        className: styles.colActions,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        })
      },
      {
        title: t('Tenders.published'),
        dataIndex: 'availableDate',
        key: TendersSortName.published,
        render: (_, record) => <Published published={record.published} />,
        sorter: true,
        sortOrder: mapSortingToSortOrder(TendersSortName.published, sorting),
        className: styles.colPublished,
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: {
          title:
            mapSortingToSortOrder(TendersSortName.published, sorting) === 'descend'
              ? t('Common.clickToSortAscending')
              : t('Common.clickToSortDescending')
        }
      },
      {
        title: t('Common.title'),
        key: TendersSortName.title,
        dataIndex: 'name',
        sorter: true,
        sortOrder: mapSortingToSortOrder(TendersSortName.title, sorting),
        className: styles.colName,
        ellipsis: true,
        render: (_, record) => (
          <span className={styles.nameLink}>
            {<TenderStatusBadge className={styles.tenderStatusBadge} statuses={record.procurementStatus} />}
            <Tooltip title={record.name}>{record.name}</Tooltip>
          </span>
        )
      },
      {
        title: t('Tenders.buyer'),
        key: TendersSortName.buyer,
        dataIndex: 'buyerBoxes',
        sorter: true,
        sortOrder: mapSortingToSortOrder(TendersSortName.buyer, sorting),
        className: styles.colBuyer,
        ellipsis: true,
        render: (_, record) => <span>{record.buyerName}</span>
      },
      {
        title: t('Tenders.deadline'),
        key: TendersSortName.deadline,
        dataIndex: 'deadline',
        render: (_, record) => <Deadline deadline={record.deadline} />,
        sorter: true,
        sortOrder: mapSortingToSortOrder(TendersSortName.deadline, sorting),
        className: styles.colDeadline
      },
      {
        title: '',
        key: 'isStarred',
        dataIndex: 'isStarred',
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        }),
        render: (_, record) => {
          const { id, isStarred, isFilledData, assignedTo, isInProgress, isCommented, name } = record;
          return (
            <>
              <ul className={styles.infoActions}>
                <li className={classNames(styles.infoAction, styles.avatar, { [styles.isHidden]: !assignedTo })}>
                  {assignedTo && <Avatar user={assignedTo} showTooltip size={'xs'} />}
                </li>
                <li
                  className={classNames(styles.infoAction, styles.comment, {
                    [styles.isHidden]: !isCommented
                  })}
                >
                  <button
                    className={styles.commentsButton}
                    onClick={() => onGoToPreview(record.id, ActivePreviewTab.Comments)}
                  >
                    <FontAwesomeIcon icon={faCommentDots} />
                  </button>
                </li>
                <li className={classNames(styles.infoAction, styles.star, { [styles.isHidden]: !isStarred })}>
                  <StarToggler isStarred={isStarred} onSetStarred={onSetStarred} id={id} name={name} />
                </li>
                <li className={classNames(styles.infoAction, styles.report)}>
                  {(isFilledData || isInProgress) && (
                    <TenderReportBadge isFilledData={isFilledData} isInProgress={isInProgress} />
                  )}
                </li>
              </ul>
            </>
          );
        },
        className: styles.colInfoActions
      }
    ];
  }, [t, sorting, onGoToPreview, onSetStarred]);
}
const components: TableProps<TendersTableItem>['components'] = {
  body: {
    row: RowComponent
  }
};

export const TendersList: FC = () => {
  const [selectedTenders, setSelectedTenders] = useSelectedTenders();
  const [{ vars: searchArgs, initVars, loading, fetchMore, fetchingMore }, setSorting] = useApiTenders();
  const { data: tendersData } = useTendersTable();
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  const fromProcurementId = useCurrentFromProc();

  const data = useMemo(() => tendersData?.tenders ?? [], [tendersData]);

  const total = useMemo(() => tendersData?.count || 0, [tendersData]);
  const hasMore = useMemo(() => {
    return data.length < total;
  }, [data.length, total]);

  const overviewMode = useOverviewMode();
  const showAdvancedSearch = overviewMode?.tab === ProcurementsDisplay.ALL;

  const loadMore = useCallback(
    (variables: { offset: number }) => {
      return fetchMore({
        variables
      });
    },
    [fetchMore]
  );

  const offset = useMemo(() => {
    return getNextOffset(data.length, total, initVars.amount ?? 0);
  }, [data.length, initVars.amount, total]);

  const onLoadMore = useFetchMore(offset, loadMore);

  const currentSidebar = useCurrentSidebar();
  const [updateTenderIsStarred] = useUpdateIsStarredTender();

  const openTenderPreview = useOpenTenderPreview();

  const onGoToPreview = useCallback(
    (procurementId: string, tab?: ActivePreviewTab) => {
      openTenderPreview(procurementId, tab);
    },
    [openTenderPreview]
  );

  const onSetStarred = useCallback(
    (id: string, name: string, starred: boolean) => {
      updateTenderIsStarred(id, name, starred, 'TendersList');
    },
    [updateTenderIsStarred]
  );

  const columns = useTableColumns(onSetStarred, onGoToPreview, searchArgs.sort);

  const onChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, (Key | boolean)[] | null>,
      sorter: SorterResult<TendersTableItem> | SorterResult<TendersTableItem>[]
    ): void => {
      if (Array.isArray(sorter)) {
        console.error('sorter is array. We do not support this');
      } else {
        const sortData = prepareSortData(sorter);
        setSorting({ [TendersSearchParams.sort]: sortData });
      }
    },
    [setSorting]
  );

  const onSelectChange = useCallback(
    selectedKeys => {
      setSelectedTenders(data.filter(tender => selectedKeys.some((key: string) => key === tender.id)));
    },
    [data, setSelectedTenders]
  );

  const rowSelectionConfig: TableRowSelection<TendersTableItem> = {
    onChange: onSelectChange,
    preserveSelectedRowKeys: false,
    selectedRowKeys: selectedTenders.map(tender => tender.id),
    renderCell: (_, record, index, originNode) => (
      <div className={styles.colActions}>
        {originNode}
        <OverviewActionsSingle procurement={record} />
      </div>
    )
  };

  useEffect(() => {
    if (fromProcurementId) {
      setTimeout(() => scrollToItem(fromProcurementId), 0);
    }
  }, [fromProcurementId]);

  const onRow: GetComponentProps<TendersTableItem> = useCallback(
    record => {
      return {
        id: record.id,
        className: record.isRead ? styles.isRead : '',
        record,
        tabIndex: 0,
        onClick: () => onGoToPreview(record.id),
        onKeyPress: () => onGoToPreview(record.id)
      };
    },
    [onGoToPreview]
  );

  const selectionIsActive = selectedTenders.length > 0;
  const onHeaderRow: GetComponentProps<readonly ColumnType<TendersTableItem>[]> = useCallback(() => {
    return {
      className: classNames(styles.headerRow, { [styles.selectionIsActive]: selectionIsActive })
    };
  }, [selectionIsActive]);

  const rowClassName = useCallback(
    (record: TendersTableItem): string =>
      classNames(styles.row, record.id === currentSidebar?.id ? 'ant-table-row-selected' : null, {
        [styles.selectionIsActive]: selectionIsActive,
        [styles.isRejected]: record.isRejected,
        [styles.isInWorkspace]: !!record.bid
      }),
    [currentSidebar, selectionIsActive]
  );

  const isEmptyFilter = useMemo(() => {
    return !isSearchApplied(searchArgs, initVars) && !!tendersData && !tendersData.count;
  }, [initVars, searchArgs, tendersData]);

  const isEmptySearch = useMemo(() => {
    return isSearchApplied(searchArgs, initVars) && !!tendersData && !tendersData.count;
  }, [initVars, searchArgs, tendersData]);

  const isLaptop = useMemo(() => {
    return width <= 1335 && width > 1024;
  }, [width]);
  const isTablet = useMemo(() => {
    return width <= 1024 && width >= 791;
  }, [width]);
  const isMobile = useMemo(() => {
    return width < 790;
  }, [width]);

  const [currentView, setTendersView] = useTendersViewState();

  const toggleView = useCallback(
    (view: PageView) => {
      setTendersView(view);
    },
    [setTendersView]
  );

  return (
    <>
      {!!showAdvancedSearch && (
        <div className={styles.heading}>
          <TendersTotal />
          <div className={styles.headingActions}>
            <FilterStatuses />
          </div>
          <div className={styles.headingActions}>
            <ViewOptionsDropDown onToggleView={toggleView} currentView={currentView} />
          </div>
        </div>
      )}
      <div className={styles.container} ref={setContainer}>
        {loading ? (
          <Skeleton loading active />
        ) : (
          <>
            {isEmptyFilter ? (
              <EmptyMessage />
            ) : isEmptySearch ? (
              <EmptySearchMessage />
            ) : (
              <div
                className={classNames(styles.content, {
                  [styles.isLaptop]: isLaptop,
                  [styles.isTablet]: isTablet,
                  [styles.isMobile]: isMobile
                })}
                ref={ref}
              >
                <Table
                  columns={columns}
                  dataSource={data}
                  rowClassName={rowClassName}
                  locale={{
                    emptyText: <></>,
                    triggerDesc: t('Common.clickToSortDescending'),
                    triggerAsc: t('Common.clickToSortAscending'),
                    cancelSort: t('Common.clickToCancelSorting')
                  }}
                  onHeaderRow={onHeaderRow}
                  rowKey={'id'}
                  rowSelection={rowSelectionConfig}
                  pagination={false}
                  onChange={onChange}
                  components={components}
                  onRow={onRow}
                  size={'large'}
                  footer={() => (
                    <LoadMoreButton
                      onLoadMore={onLoadMore}
                      isLoading={fetchingMore}
                      hasMore={hasMore}
                      scrollContainer={container}
                    />
                  )}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default TendersList;
